import { SearchOutlined } from '@ant-design/icons'
import { Divider, Row, Space } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import useAddressSearch from 'app/components/Core/Checkout/hooks/useAddressSearch'
import CheckoutMap from 'app/components/Core/Checkout/Map'
import ProjectList from 'app/components/Core/Project/List'
import useDeliveryAddressForm from 'app/components/Core/Rental/hooks/useDeliveryAddressForm'
import KForm from 'app/components/Modules/Form/_KForm'
import KReadOnlyInput from 'app/components/Modules/Form/KReadOnlyInput'
import Container from 'app/components/UI-Elements/Layout/Container'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import KFooter from 'app/components/UI-Elements/Layout/KFooter'
import { RecordType } from 'app/constants/GlobalTypes'
import rentalNormalizer, { normalizeAddressSummary } from 'app/lib/apollo/normalizers/Rental'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { useQuery } from 'app/lib/hooks/useQuery'
import useSetBackHistory from 'app/lib/hooks/useSetBackHistory'
import useTitle from 'app/lib/hooks/useTitle'
import useTranslation from 'app/lib/hooks/useTranslation'
import { mapProject } from 'app/lib/utils/delivery-address'
import { PageProps } from 'app/pages/typing'
import { RecordTypeEnum } from 'app/queries'
import * as rentalsCheckout from 'app/queries/Rentals/Checkout'
import { Routes } from 'app/routes/Routes'
import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import CheckoutStepper from '../Component/Stepper'

const portalId = 'checkout-address-portal-id'

const CheckoutDeliveryAddress: React.FC<PageProps> = (props) => {
  useTitle(props)
  const { md } = useBreakpoint()
  const { checkout_id: rental_id } = useParams<{ checkout_id: string }>()
  const { setBackAction } = useBrowserRouterContext()
  const { open: openAddressFormDrawer } = useDeliveryAddressForm(rental_id)
  const {
    modalify: { open, close: closeModal }
  } = useFeedbacker()
  const { tRoute, tRecordType } = useTranslation({
    scope: props.route.key,
    recordType: RecordTypeEnum.rental
  })
  const { tModal } = useTranslation({
    recordType: RecordType.checkout
  })

  const {
    data: { rental }
  } = useQuery<{ rental: IRental }>(rentalsCheckout.RENTAL_CHECKOUT_QUERY, {
    variables: {
      id: rental_id
    },
    normalizer: (data) => ({ rental: rentalNormalizer(data.rental) })
  })
  const navigate = useNavigate()
  const addressSearch = useAddressSearch()

  useSetBackHistory(() => {
    setBackAction(onBack)
  })

  const onBack = () => {
    const onClose = () => {
      setBackAction(onBack)
      closeModal()
    }

    open({
      modalType: 'confirm',
      recordType: RecordType.checkout,
      action: 'cancel_request',
      content: tModal({
        id: 'cancel_request'
      }),
      onConfirm: () => navigate(Routes.Rental.collection().index),
      onClose,
      destroyOnClose: true
    })
  }

  const onAddressFocus = () => addressSearch.open({ rental, showAvailabilityHint: false })

  const onFinish = () => navigate(Routes.Checkout.member(rental_id).machines)

  const onProjectClick = (project: IProject) => {
    openAddressFormDrawer({
      member: { ...rental, ...mapProject(project) },
      props: { allowProjectSelection: false }
    })
  }

  return (
    <Container>
      <Row gutter={[0, 12]}>
        <KCol span={24}>
          <CheckoutStepper activeStep={0} />
        </KCol>
        <KCol span={24} loading={!rental} className="mb-4">
          <If condition={rental}>
            <KForm
              layout="vertical"
              recordType={RecordTypeEnum.rentalDeliveryAddress}
              onFinish={onFinish}
              visualAction="toMachineOverview"
              initialValues={{ addressSummary: normalizeAddressSummary(rental.deliveryAddress) }}
              portalId={portalId}>
              <Row gutter={[12, 12]}>
                <KCol xs={24} md={10}>
                  <Row gutter={[0, 12]}>
                    <KCol span={24}>
                      <KReadOnlyInput
                        readOnly
                        required
                        name="addressSummary"
                        onClick={onAddressFocus}
                        hasPlaceholder
                        label={tRoute({ id: 'machineAddress' })}
                        suffix={<SearchOutlined />}
                        value={normalizeAddressSummary(rental.deliveryAddress)}
                      />
                    </KCol>
                    <KCol span={24}>
                      <ProjectList
                        props={{
                          loading: false,
                          onItemClick: onProjectClick,
                          pagination: false,
                          displayHeader: true,
                          header: (
                            <Space direction="vertical" className="full-width">
                              <span>{tRecordType({ id: 'existing_delivery_addresses' })}</span>
                              <Divider className="my-0" />
                            </Space>
                          ),
                          params: {
                            per_page: 3,
                            filters: { with_name: true },
                            sort: 'name'
                          }
                        }}
                      />
                    </KCol>
                  </Row>
                </KCol>
                <KCol xs={24} md={14}>
                  <Row gutter={[0, { xs: 0, md: 56 }]}>
                    <KCol span={24} />
                    <KCol span={24}>
                      <CheckoutMap member={rental} props={{ height: md ? '300px' : '200px' }} />
                    </KCol>
                  </Row>
                </KCol>
              </Row>
            </KForm>
          </If>
        </KCol>
      </Row>
      <KFooter sticky={!md} removePadding={md}>
        <div id={portalId} />
      </KFooter>
    </Container>
  )
}

export default CheckoutDeliveryAddress
